<template>
  <CompanyAppLayout 
    page-title="Store" 
    page-subtitle="Browse safety equipment, tools, and training materials for oilfield companies"
    :show-cart-button="true"
    :cart-item-count="cartItemCount"
    @cart-click="showCart = true"
  >
    <!-- Store Content -->
    <div class="space-y-8 pt-8">
      <!-- Store Header -->
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-white mb-2">OilField Equipment Store</h2>
          <p class="text-slate-300">Professional safety equipment, tools, and training materials</p>
        </div>
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search products..." 
              class="w-64 px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
            <svg class="absolute right-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="border-b border-slate-700/50">
        <nav class="flex space-x-8">
          <button 
            v-for="tab in tabs" 
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
              activeTab === tab.id 
                ? 'border-emerald-500 text-emerald-400' 
                : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600'
            ]"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
          <p class="text-slate-400">Loading products...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
        <p class="text-red-400 mb-4">{{ error }}</p>
        <button @click="fetchProducts" class="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg">
          Try Again
        </button>
      </div>

      <!-- Tab Content -->
      <div v-else class="min-h-96">
        <!-- Safety Equipment Tab -->
        <div v-if="activeTab === 'safety'" class="space-y-6">
          <p v-if="amazonError && !filteredSafetyEquipment.length" class="text-red-400 text-center">{{ amazonError }}</p>
          <div v-if="filteredSafetyEquipment.length === 0" class="text-center py-12">
            <p class="text-slate-400">{{ amazonError || 'No Amazon safety equipment found.' }}</p>
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="item in filteredSafetyEquipment" 
              :key="item.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-video bg-gradient-to-br from-red-900/20 to-orange-900/20 relative overflow-hidden">
                <img 
                  :src="item.image" 
                  :alt="item.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-red-500/90 text-white text-xs font-medium rounded-full">
                    {{ item.category }}
                  </span>
                </div>
                <div v-if="item.stock <= 5 && item.stock > 0" class="absolute top-3 left-3">
                  <span class="px-2 py-1 bg-amber-500/90 text-white text-xs font-medium rounded-full">
                    Low Stock
                  </span>
                </div>
                <div v-if="item.stock === 0" class="absolute top-3 left-3">
                  <span class="px-2 py-1 bg-red-500/90 text-white text-xs font-medium rounded-full">
                    Out of Stock
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {{ item.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ item.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center">
                      <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      <span class="text-sm text-slate-400 ml-1">{{ item.rating }}</span>
                    </div>
                    <span class="text-slate-500">•</span>
                    <span class="text-sm text-slate-400">{{ item.brand }}</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ item.price.toLocaleString() }}</span>
                </div>
                
                <div v-if="!item.affiliateUrl" class="flex items-center justify-between mb-3">
                  <span class="text-sm text-slate-400">Stock: {{ item.stock }}</span>
                  <span class="text-sm text-slate-400">SKU: {{ item.sku }}</span>
                </div>
                
                <a
                  v-if="item.affiliateUrl"
                  :href="item.affiliateUrl"
                  target="_blank"
                  rel="noopener"
                  class="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center"
                >View on Amazon</a>
                <button 
                  v-else
                  @click="addToCart(item)"
                  :disabled="item.stock === 0"
                  class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>{{ item.stock === 0 ? 'Out of Stock' : 'Add to Cart' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tools Tab -->
        <div v-if="activeTab === 'tools'" class="space-y-6">
          <div v-if="filteredTools.length === 0" class="text-center py-12">
            <p class="text-slate-400">{{ amazonError || 'No Amazon tools found.' }}</p>
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="item in filteredTools" 
              :key="item.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-video bg-gradient-to-br from-blue-900/20 to-purple-900/20 relative overflow-hidden">
                <img 
                  :src="item.image" 
                  :alt="item.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-blue-500/90 text-white text-xs font-medium rounded-full">
                    {{ item.category }}
                  </span>
                </div>
                <div v-if="item.stock <= 5 && item.stock > 0" class="absolute top-3 left-3">
                  <span class="px-2 py-1 bg-amber-500/90 text-white text-xs font-medium rounded-full">
                    Low Stock
                  </span>
                </div>
                <div v-if="item.stock === 0" class="absolute top-3 left-3">
                  <span class="px-2 py-1 bg-red-500/90 text-white text-xs font-medium rounded-full">
                    Out of Stock
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {{ item.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ item.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center">
                      <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      <span class="text-sm text-slate-400 ml-1">{{ item.rating }}</span>
                    </div>
                    <span class="text-slate-500">•</span>
                    <span class="text-sm text-slate-400">{{ item.brand }}</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ item.price.toLocaleString() }}</span>
                </div>
                
                <div v-if="!item.affiliateUrl" class="flex items-center justify-between mb-3">
                  <span class="text-sm text-slate-400">Stock: {{ item.stock }}</span>
                  <span class="text-sm text-slate-400">SKU: {{ item.sku }}</span>
                </div>
                
                <a
                  v-if="item.affiliateUrl"
                  :href="item.affiliateUrl"
                  target="_blank"
                  rel="noopener"
                  class="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center"
                >View on Amazon</a>
                <button 
                  v-else
                  @click="addToCart(item)"
                  :disabled="item.stock === 0"
                  class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>{{ item.stock === 0 ? 'Out of Stock' : 'Add to Cart' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'amazon'" class="space-y-6">
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Search Term</label>
                <input
                  v-model="amazonSearchParams.keywords"
                  type="text"
                  placeholder="e.g., safety equipment, tools..."
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select
                  v-model="amazonSearchParams.category"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="">All Categories</option>
                  <option value="tools">Tools & Equipment</option>
                  <option value="safety">Safety Equipment</option>
                  <option value="clothing">Work Clothing</option>
                  <option value="electronics">Electronics</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Sort By</label>
                <select
                  v-model="amazonSearchParams.sortBy"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Customer Rating</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              class="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-6 rounded-lg font-medium"
              @click="searchAmazonProducts"
            >Search Amazon</button>
          </div>

          <div v-if="isLoadingAmazon" class="text-center py-12 text-slate-300">Searching Amazon products…</div>
          <div v-else-if="!amazonProducts.length" class="text-center py-12 text-slate-400">{{ amazonError || 'No Amazon products found.' }}</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div
              v-for="product in amazonProducts"
              :key="product.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300"
            >
              <div class="aspect-square relative overflow-hidden">
                <img :src="product.image" :alt="product.title" class="w-full h-full object-cover" />
                <span class="absolute top-3 right-3 px-2 py-1 bg-orange-500/90 text-white text-xs font-medium rounded-full">Amazon</span>
              </div>
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 line-clamp-2">{{ product.title }}</h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ product.description }}</p>
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm text-slate-400">{{ product.rating }} · {{ product.reviewCount }} reviews</span>
                  <span class="text-lg font-bold text-emerald-400">${{ product.price }}</span>
                </div>
                <a
                  :href="product.affiliateUrl"
                  target="_blank"
                  class="block text-center bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium"
                >View on Amazon</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Training Tab -->
        <div v-if="activeTab === 'training'" class="space-y-6">
          <div v-if="filteredTraining.length === 0" class="text-center py-12">
            <p class="text-slate-400">{{ amazonError || 'No Amazon training products found.' }}</p>
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div 
              v-for="item in filteredTraining" 
              :key="item.id"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div class="aspect-video bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 relative overflow-hidden">
                <img 
                  :src="item.image" 
                  :alt="item.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div class="absolute top-3 right-3">
                  <span class="px-2 py-1 bg-emerald-500/90 text-white text-xs font-medium rounded-full">
                    {{ item.level }}
                  </span>
                </div>
              </div>
              
              <div class="p-4">
                <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {{ item.title }}
                </h3>
                <p class="text-slate-300 text-sm mb-3 line-clamp-2">{{ item.description }}</p>
                
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center">
                      <svg class="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                      <span class="text-sm text-slate-400 ml-1">{{ item.rating }}</span>
                    </div>
                    <span class="text-slate-500">•</span>
                    <span class="text-sm text-slate-400">{{ item.duration }}</span>
                  </div>
                  <span class="text-lg font-bold text-emerald-400">${{ item.price.toLocaleString() }}</span>
                </div>
                
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm text-slate-400">Format: {{ item.format }}</span>
                  <span class="text-sm text-slate-400">SKU: {{ item.sku }}</span>
                </div>
                
                <a
                  v-if="item.affiliateUrl"
                  :href="item.affiliateUrl"
                  target="_blank"
                  rel="noopener"
                  class="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white py-2 px-4 rounded-lg font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 flex items-center justify-center"
                >View on Amazon</a>
                <button 
                  v-else
                  @click="addToCart(item)"
                  class="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white py-2 px-4 rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <span>Add to Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cart Sidebar -->
    <CartSidebar 
      :is-open="showCart" 
      @close="showCart = false"
      @order-complete="handleCheckout"
    />
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import CartSidebar from '@/components/store/CartSidebar.vue'
import { amazonService, type AmazonProduct, type AmazonSearchParams } from '@/services/amazonService'
import { storeService } from '@/services/storeService'
import { useUserStore } from '@/stores/userStore'
import { computed, onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const searchQuery = ref('')
const activeTab = ref('safety')
const showCart = ref(false)
const isLoading = ref(false)
const isLoadingAmazon = ref(false)
const error = ref('')
const amazonError = ref('')
const amazonProducts = ref<AmazonProduct[]>([])
const amazonSearchParams = ref({
  keywords: '',
  category: '',
  sortBy: 'relevance',
})

// Product data from API
const safetyEquipment = ref<any[]>([])
const tools = ref<any[]>([])
const training = ref<any[]>([])

// Tab configuration
const tabs = ref([
  { id: 'safety', name: 'Safety Equipment' },
  { id: 'tools', name: 'Tools & Equipment' },
  { id: 'amazon', name: 'Get It On Amazon' },
  { id: 'training', name: 'Training Materials' }
])

// Computed properties
const filteredSafetyEquipment = computed(() => {
  return safetyEquipment.value.filter(item => 
    item.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const filteredTools = computed(() => {
  return tools.value.filter(item => 
    item.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.category.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const filteredTraining = computed(() => {
  return training.value.filter(item => 
    item.title.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    item.level.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const cartItemCount = computed(() => {
  return storeService.getCart().value.length
})

// Methods
const fetchProducts = async () => {
  try {
    isLoading.value = true
    error.value = ''
    amazonError.value = ''
    const [safety, toolItems, trainingItems] = await Promise.all([
      amazonService.searchCatalog({ keywords: '', category: 'safety' }),
      amazonService.searchCatalog({ keywords: '', category: 'tools' }),
      amazonService.searchCatalog({ keywords: '', category: 'training' })
    ])
    safetyEquipment.value = safety.products
    tools.value = toolItems.products
    training.value = trainingItems.products
    amazonError.value = safety.error || toolItems.error || trainingItems.error
  } catch (err) {
    console.error('Error fetching products:', err)
    amazonError.value = 'Amazon catalog is unavailable.'
  } finally {
    isLoading.value = false
  }
}

const addToCart = async (item: any) => {
  try {
    isLoading.value = true
    
    // Add to cart service
    storeService.addToCart(item, 'product')
    
    // Update stock in real-time (only for items with stock property)
    if ('stock' in item) {
      await updateStock(item.id, item.stock - 1)
    }
    
    // Show success notification
    storeService.showNotification(`${item.title} added to cart!`)
    
  } catch (error) {
    console.error('Error adding to cart:', error)
    storeService.showNotification('Error adding item to cart')
  } finally {
    isLoading.value = false
  }
}

const updateStock = async (itemId: number, newStock: number) => {
  try {
    const response = await fetch(`/api/store/update-stock`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ itemId, newStock })
    })
    
    if (response.ok) {
      // Update local stock
      const allItems = [...safetyEquipment.value, ...tools.value, ...training.value]
      const item = allItems.find(i => i.id === itemId)
      if (item && 'stock' in item) {
        (item as any).stock = newStock
      }
    }
  } catch (error) {
    console.error('Error updating stock:', error)
  }
}

const handleCheckout = async () => {
  try {
    isLoading.value = true
    
    // Process payment through store service
    await storeService.processPayment('credit_card')
    
    // Clear cart after successful payment
    storeService.clearCart()
    
    // Show success message
    storeService.showNotification('Order completed successfully!')
    
    showCart.value = false
    
  } catch (error) {
    console.error('Checkout error:', error)
    storeService.showNotification('Payment failed. Please try again.')
  } finally {
    isLoading.value = false
  }
}

async function searchAmazonProducts() {
  isLoadingAmazon.value = true
  try {
    const result = await amazonService.searchCatalog({
      keywords: amazonSearchParams.value.keywords,
      category: amazonSearchParams.value.category || undefined,
      sortBy: amazonSearchParams.value.sortBy as AmazonSearchParams['sortBy'],
    })
    amazonProducts.value = result.products
    if (result.error) amazonError.value = result.error
  } catch (e) {
    console.error('Error searching Amazon products:', e)
    amazonProducts.value = []
    amazonError.value = 'Amazon catalog is unavailable.'
  } finally {
    isLoadingAmazon.value = false
  }
}

// Lifecycle
onMounted(async () => {
  try {
    // Load cart from localStorage
    storeService.loadCartFromStorage()
    
    // Fetch real product data from API
    await Promise.all([fetchProducts(), searchAmazonProducts()])
    
  } catch (error) {
    console.error('Error loading store data:', error)
  }
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
